import React, { useState, useRef } from "react";

import { Drawer } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BsThreeDots } from "react-icons/bs";
import { MdClose, MdOutlinePhoto } from "react-icons/md";

import ImageCarouselModal, {
  ImageCarouselModalHandle,
  File,
} from "../../../../../../../components/ImageCarouselModal";
import { Screenshot } from "../../../../../../../models/screenshot";
import ScreenshotService from "../../../../../../../services/screenshot.service";
import { FileType } from "../../../../../../../util/enums";
import useStyles from "./styles";

interface Props {
  taskId?: number;
}

const ScreenshotsList = ({ taskId }: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const carouselImgRef = useRef<ImageCarouselModalHandle>(null);

  const [screenshots, setScreenshots] = useState<Screenshot[]>([]);
  const [seeCountImages] = useState<number>(2);
  const [openDrawerImage, setOpenDrawerImage] = useState<boolean>(false);

  const { isFetching } = useQuery(
    [`screenshot.list.${taskId}`],
    () =>
      ScreenshotService.getAll({
        taskId,
      }),
    {
      enabled: !!taskId,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setScreenshots(response.data);
      },
    }
  );

  const onOpenImgModal = (imgIndex: number) => {
    const screenshotFiles = screenshots.map(
      (screenshot) => ({ url: screenshot.link, type: FileType.IMAGE } as File)
    );

    carouselImgRef?.current?.handleOpenModal(imgIndex, screenshotFiles);
  };

  const returnScreenshotImages = () => {
    return screenshots.map((image, index: number) => (
      <img
        className={
          openDrawerImage
            ? classes.image
            : `${index < seeCountImages ? "d-block" : "d-none"} ${classes.image}`
        }
        key={index}
        src={image.link || ""}
        alt={translate("imageOfAScreenshot")}
        onClick={() => onOpenImgModal(index)}
      />
    ));
  };

  return (
    <div>
      <ImageCarouselModal ref={carouselImgRef} />
      <span className={classes.screenshotTitle}>{translate("screenshots")}</span>
      {isFetching ? (
        <div className={`${classes.uploadProgress} withAnimation`}>
          <MdOutlinePhoto size={24} />
          <span>{translate("fetchingScreenshots")}</span>
        </div>
      ) : (
        <div className={classes.imageWrapper}>
          {screenshots && screenshots.length > 0 ? (
            <>
              {returnScreenshotImages()}
              {screenshots.length > 2 && (
                <div className={classes.countImageButton} onClick={() => setOpenDrawerImage(true)}>
                  <BsThreeDots size={16} />
                  <span>{translate("seeMore")}</span>
                </div>
              )}
            </>
          ) : (
            <div className={classes.uploadProgress}>
              <MdOutlinePhoto size={24} />
              <span>{translate("noScreenshotAvailable")}</span>
            </div>
          )}
        </div>
      )}
      <Drawer
        anchor="right"
        open={openDrawerImage}
        onClose={() => setOpenDrawerImage(false)}
        className={classes.drawer}
      >
        <header className={classes.drawerHeader}>
          <div>
            <span>{translate("screenshots")}</span>
            <MdClose size={18} color="#fff" onClick={() => setOpenDrawerImage(false)} />
          </div>
        </header>
        <div className={`${classes.imageWrapper} p-2 px-4`}>{returnScreenshotImages()}</div>
      </Drawer>
    </div>
  );
};

export default ScreenshotsList;
